import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/datatable.scss";
import { accountKindLabel, padCategoryLabel } from "../../../utils/orderAccountKind";
import {
  blockerLabel,
  rowCategory,
  selectionCategory,
  filterByCategory,
  generateRefusalText,
  loadRefusalText,
  fileRefusalText,
  readErrorBody,
  CATEGORY_FILTERS,
  ONE_CATEGORY_NOTE,
  UNRESOLVED_SELECTION_NOTE,
} from "./achFilesUi";

/**
 * ACH Files — the admin side of the orchestrated RBC ACH pipeline (B3).
 *
 * 1. Eligible investments: every ACH-routed UNPAID order with per-row blocker
 *    flags (PAD authorization and binding, bank coordinates, signed documents,
 *    suitability, account kind, company bank verification), each worded by
 *    achFilesUi. Only fully eligible rows can be pulled into a file. Each row
 *    carries its PAD category and account kind (corporate Business PAD
 *    contract, 2026-10-25), and the grid filters by category.
 * 2. Generate: the API rebuilds the payment list server-side and calls the ACH
 *    service; replays of the same batch return the stored file instead of
 *    minting a new one. Neither the eligible list nor the generate answer
 *    carries an account number. The DOWNLOADED file is the RBC payload itself,
 *    so it does carry the full coordinates: handle it as the bank file it is.
 *    (The old note here said full account numbers never reach this browser;
 *    the download always did, and until I9 the investment view's XLSX export
 *    did too.)
 *    I6: one file holds one PAD category, so a selection that spans two, or
 *    holds a row with no known category, is not sent, and the API refuses a
 *    mixed one with 409 MIXED_PAD_CATEGORIES.
 * 3. Files: lifecycle GENERATED → UPLOADED_TO_RBC → SETTLED / RETURNED (or
 *    VOID before upload). Settling marks the included investments PAID;
 *    returns revert the returned subset to UNPAID for a retry file.
 * Refusals (Amendments A4.3 and A5): a 503 FLAG_STATE_UNKNOWN on any call, and
 * a 409 ACH_FILE_ENTRIES_BLOCKED on download or upload (every entry is
 * re-checked before the file goes to RBC), are worded by achFilesUi, never
 * with the server's text. The download's error body is a Blob (readErrorBody).
 */
const API = process.env.REACT_APP_API;

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

const AchFiles = () => {
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [category, setCategory] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [inv, fl] = await Promise.all([
        axios.post(`${API}admin/ach/eligible-investments`, {}, { headers: authHeaders() }),
        axios.get(`${API}admin/ach/files`, { headers: authHeaders() }),
      ]);
      setEligible(inv?.data?.investments || []);
      setFiles(fl?.data?.files || []);
    } catch (error) {
      const body = error?.response?.data;
      const worded = loadRefusalText(body);
      Swal.fire({
        title: worded ? "ACH data could not be loaded" : body?.message || "Failed to load ACH data",
        ...(worded ? { text: worded } : {}),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // I6: only the rows the category filter shows can be selected, and a
  // selection is sent only when every row in it carries the same known PAD
  // category: never one that spans two, nor one with a row of no category.
  const visible = filterByCategory(eligible, category);
  const selection = selectionCategory(eligible, selected);
  const sendable = selection === "PERSONAL" || selection === "BUSINESS";
  const mixedSelection = selection === "MIXED";
  const unresolvedSelection = selection === "UNRESOLVED";

  const changeCategory = (value) => {
    setCategory(value);
    const keep = new Set(filterByCategory(eligible, value).map((r) => r.investmentId));
    setSelected((sel) => sel.filter((id) => keep.has(id)));
  };

  const handleGenerate = async () => {
    if (!sendable) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/ach/generate-file`,
        { investmentIds: selected, dueDate },
        { headers: authHeaders() }
      );
      const file = res?.data?.file;
      Swal.fire({
        title: res?.data?.replayed ? "File already generated (replay)" : "ACH file generated",
        text: file ? `${file.fileName} — ${file.entryCount} entries, $${file.totalAmount}` : "",
        icon: "success",
        confirmButtonText: "OK",
      });
      setSelected([]);
      await fetchAll();
    } catch (error) {
      // MIXED_PAD_CATEGORIES, BUSINESS_PAD_PROFILE_NOT_CONFIGURED and the
      // per-order blockers are worded, never shown as raw codes or keys.
      const body = error?.response?.data;
      Swal.fire({
        title: "ACH generation failed",
        text: body ? generateRefusalText(body, eligible) : error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}admin/ach/files/${file.id}/download`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      // The body of a refused Blob request is a Blob: read it for the code.
      const body = await readErrorBody(error?.response?.data);
      const worded = fileRefusalText(body, file, "download", eligible);
      Swal.fire({ title: "Download failed", ...(worded ? { text: worded } : {}), icon: "error", confirmButtonText: "OK" });
    }
  };

  const transition = async (file, status) => {
    let body = { status };
    if (status === "RETURNED") {
      const { value } = await Swal.fire({
        title: "Returned entries",
        input: "text",
        inputLabel: "Comma-separated investment IDs that were returned by RBC",
        showCancelButton: true,
      });
      if (!value) return;
      body.returns = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((id) => ({ investmentId: Number(id), reason: "RBC return" }));
    } else {
      const confirm = await Swal.fire({
        title: `Mark file as ${status.replace(/_/g, " ")}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });
      if (!confirm.isConfirmed) return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API}admin/ach/files/${file.id}/status`, body, {
        headers: authHeaders(),
      });
      await fetchAll();
    } catch (error) {
      const refused = error?.response?.data;
      const worded = fileRefusalText(refused, file, status === "UPLOADED_TO_RBC" ? "upload" : "status", eligible);
      Swal.fire({
        title: worded ? "Status update failed" : refused?.message || "Status update failed",
        ...(worded ? { text: worded } : {}),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const eligibleColumns = [
    { field: "investmentId", headerName: "ID", width: 80 },
    { field: "investor", headerName: "Investor", flex: 1, minWidth: 160 },
    { field: "campaign", headerName: "Campaign", flex: 1, minWidth: 180 },
    { field: "amount", headerName: "Amount", width: 110 },
    { field: "referenceCode", headerName: "Reference", width: 150 },
    {
      field: "padCategory",
      headerName: "PAD category",
      width: 200,
      renderCell: (params) => (
        <span data-pad-category={rowCategory(params.row)}>
          {padCategoryLabel(params.row.padCategory)}, {accountKindLabel(params.row.accountKind)}
        </span>
      ),
    },
    {
      field: "eligible",
      headerName: "Blockers",
      flex: 1,
      minWidth: 220,
      renderCell: (params) =>
        params.row.eligible ? (
          <Chip label="Ready" color="success" size="small" />
        ) : (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {Object.entries(params.row.blockers || {})
              .filter(([, ok]) => !ok)
              .map(([k]) => (
                <Chip key={k} label={blockerLabel(k, params.row)} color="warning" size="small" />
              ))}
          </div>
        ),
    },
  ];

  const fileColumns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "file_name", headerName: "File", flex: 1, minWidth: 220 },
    { field: "purpose", headerName: "Purpose", width: 190 },
    {
      field: "pad_category",
      headerName: "PAD category",
      width: 140,
      renderCell: (params) => (params.row.pad_category ? padCategoryLabel(params.row.pad_category) : "-"),
    },
    { field: "direction", headerName: "Dir", width: 80 },
    { field: "entry_count", headerName: "Entries", width: 90 },
    { field: "total_amount", headerName: "Total", width: 110 },
    { field: "due_date", headerName: "Due", width: 110 },
    { field: "status", headerName: "Status", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 320,
      renderCell: (params) => {
        const f = params.row;
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => downloadFile(f)}>
              Download
            </Button>
            {f.status === "GENERATED" && (
              <>
                <Button size="small" variant="contained" onClick={() => transition(f, "UPLOADED_TO_RBC")}>
                  Mark uploaded
                </Button>
                <Button size="small" color="error" onClick={() => transition(f, "VOID")}>
                  Void
                </Button>
              </>
            )}
            {f.status === "UPLOADED_TO_RBC" && (
              <>
                <Button size="small" color="success" variant="contained" onClick={() => transition(f, "SETTLED")}>
                  Settled
                </Button>
                <Button size="small" color="warning" onClick={() => transition(f, "RETURNED")}>
                  Returned
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <PageShell>
      <PageHead title="ACH Files" />
      <div className="datatable">
        <Loader isLoading={loading} />
        <div className="datatableTitle">
          <h5>ACH — eligible investments (pre-authorized debits)</h5>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <label htmlFor="ach-due-date" style={{ fontSize: 14 }}>
              Due date
              <input
                id="ach-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
              />
            </label>
            <label htmlFor="ach-pad-category" style={{ fontSize: 14 }}>
              PAD category
              <select
                id="ach-pad-category"
                value={category}
                onChange={(e) => changeCategory(e.target.value)}
                style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
              >
                {CATEGORY_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </label>
            <Button
              variant="contained"
              color="primary"
              disabled={!sendable}
              onClick={handleGenerate}
            >
              Generate debit file ({selected.length})
            </Button>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "#555", margin: "4px 0 8px" }}>{ONE_CATEGORY_NOTE}</p>
        {mixedSelection && (
          <p role="alert" style={{ fontSize: 13, color: "#b42318", margin: "0 0 8px" }}>
            This selection mixes personal and business PADs. Filter by category and generate one file per category.
          </p>
        )}
        {unresolvedSelection && (
          <p role="alert" style={{ fontSize: 13, color: "#b42318", margin: "0 0 8px" }}>
            {UNRESOLVED_SELECTION_NOTE}
          </p>
        )}
        <DataGrid
          className="datagrid"
          autoHeight
          rows={visible}
          getRowId={(r) => r.investmentId}
          columns={eligibleColumns}
          checkboxSelection
          isRowSelectable={(params) => params.row.eligible}
          selectionModel={selected}
          onSelectionModelChange={(sel) =>
            setSelected(
              sel.filter((id) => visible.find((e) => e.investmentId === id && e.eligible))
            )
          }
          hideFooterPagination
        />

        <div className="datatableTitle" style={{ marginTop: 32 }}>
          <h5>Generated files</h5>
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={files}
          columns={fileColumns}
          hideFooterPagination
        />
      </div>
    </PageShell>
  );
};

export default AchFiles;
